/**
 * useHistory v0.3
 *
 * 指定モードの履歴を扱うフック。v0.2 までは全モード混合の単一履歴だったが、
 * v0.3 から「翻訳」「要約」「通訳」が個別の履歴を持つ。
 *
 * mode が変わると useEffect が再実行され、history も切り替わる。
 *
 * 設計上のポイント：
 *   - storage.ts 経由で読み書き（直接 localStorage は触らない）
 *   - マイグレーションは main.tsx の起動時に1回呼ぶ前提（ここでは扱わない）
 */

import { useCallback, useEffect, useState } from 'react';

import * as storage from '../lib/storage';

import type { Mode, ScanResult } from '../types';

export interface UseHistoryResult {
  history: ScanResult[];
  /** 履歴に1件追加（新しいものが先頭） */
  append: (result: ScanResult) => void;
  /** 履歴を全削除 */
  clear: () => void;
}

export function useHistory(mode: Mode): UseHistoryResult {
  const [history, setHistory] = useState<ScanResult[]>([]);

  // mode 変更で履歴を読み直す
  useEffect(() => {
    setHistory(storage.getHistory(mode));
  }, [mode]);

  const append = useCallback(
    (result: ScanResult) => {
      const next = storage.addToHistory(mode, result);
      setHistory(next);
    },
    [mode],
  );

  const clear = useCallback(() => {
    storage.clearHistory(mode);
    setHistory([]);
  }, [mode]);

  return { history, append, clear };
}
